import React, { useState } from 'react';
import { Box, TextField, InputAdornment, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { NavigationShellProps } from './types';
import { NAVIGATION_WIDTH, COLLAPSED_WIDTH, NAVIGATION_COLORS } from './constants';

const NavigationShell: React.FC<NavigationShellProps> = ({
    searchTerm,
    onSearchChange,
    searchPlaceholder,
    backgroundColor,
    storageKey,
    children,
}) => {
    const [isExpanded, setIsExpanded] = useState(() => {
        try {
            const saved = sessionStorage.getItem(storageKey);
            return saved ? JSON.parse(saved) : true;
        } catch {
            return true;
        }
    });

    const toggleExpansion = () => {
        const next = !isExpanded;
        sessionStorage.setItem(storageKey, JSON.stringify(next));
        setIsExpanded(next);
    };

    return (
        <Box sx={{ position: 'relative', height: '100vh', display: 'flex' }}>
            {/* Expanded Sidebar - Always mounted, visibility controlled by CSS */}
            <Box
                sx={{
                    width: isExpanded ? NAVIGATION_WIDTH : COLLAPSED_WIDTH,
                    minWidth: isExpanded ? NAVIGATION_WIDTH : COLLAPSED_WIDTH,
                    maxWidth: isExpanded ? NAVIGATION_WIDTH : COLLAPSED_WIDTH,
                    backgroundColor,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 0,
                    overflow: 'hidden',
                    transition: 'width 0.2s ease-in-out, min-width 0.2s ease-in-out, max-width 0.2s ease-in-out',
                }}
            >
                {/* Search Box - Hidden when collapsed */}
                <Box
                    sx={{
                        padding: '12px',
                        display: isExpanded ? 'block' : 'none',
                    }}
                >
                    <TextField
                        fullWidth
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => onSearchChange(e.target.value)}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        sx={{
                                            fontSize: 18,
                                            color: 'rgba(255, 255, 255, 0.5)',
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                color: 'rgba(255, 255, 255, 0.8)',
                                borderRadius: '4px',
                                '& fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: NAVIGATION_COLORS.TEXT_SELECTED,
                                },
                            },
                            '& .MuiOutlinedInput-input': {
                                padding: '8px 12px',
                                fontSize: '12px',
                                '&::placeholder': {
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    opacity: 1,
                                },
                            },
                        }}
                    />
                </Box>

                {/* Content Area - Hidden when collapsed but stays mounted */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        display: isExpanded ? 'block' : 'none',
                    }}
                >
                    {children}
                </Box>
            </Box>

            {/* Toggle Button */}
            <IconButton
                aria-label="toggle-sidebar"
                onClick={toggleExpansion}
                sx={{
                    position: 'absolute',
                    top: 60,
                    right: -16,
                    transform: 'translateY(-50%)',
                    backgroundColor: NAVIGATION_COLORS.TOGGLE_BG,
                    color: 'white',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    boxShadow: 1,
                    zIndex: 1,
                    '&:hover': { backgroundColor: NAVIGATION_COLORS.TOGGLE_HOVER },
                }}
            >
                {isExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
        </Box>
    );
};

export default NavigationShell;
